import { Button } from "@/components/ui/shadcn/Button/Button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/shadcn/Popover/Popover";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Connection } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { getConnectionPreviewContact } from "@/lib/utils/connections/item-contact-connections";
import { getConnectionContactName } from "@/lib/utils/connections/vehicle-contact-connections";
import { formatLocalOrIntlPhoneNumber } from "@/lib/utils/format";
import { getPrimaryItem } from "@/lib/utils/matterContactConnectionHelpers";
import { ContactEmailIcon, ContactPhoneIcon } from "@/lib/utils/iconUtils";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { Typography } from "../Typography/Typography";

export const CompactConnectionContactPreviewChip = ({
  connection,
  matterId,
}: {
  connection: Connection;
  matterId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { openCommunicationModal } = useGlobalModal();
  const contact = connection.contact;
  const preview = getConnectionPreviewContact(connection);
  const email = getPrimaryItem(preview?.emails ?? [], "isPrimary");
  const phone = getPrimaryItem(preview?.phones ?? [], "isPrimary");

  return (
    <div className="flex items-center gap-x-1">
      <Typography>{getConnectionContactName(contact)}</Typography>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="link"
            className="text-muted-foreground h-4 w-4 p-0 focus-visible:ring-0 focus-visible:ring-offset-0"
            size="icon"
            onClick={() => {
              setIsOpen(!isOpen);
            }}
          >
            <ChevronRight className={cn("h-4 w-4", isOpen && "rotate-90")} />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex w-fit flex-col items-start gap-y-1 p-2">
          <Typography size="sm" className="flex items-center gap-x-1">
            <ContactEmailIcon className="h-4 w-4" />
            {email?.address ?? (
              <Typography variant="subHeading">No email address</Typography>
            )}
          </Typography>
          <Button
            variant="link"
            size="sm"
            className="h-5 p-0"
            onClick={() => {
              setIsOpen(false);
              openCommunicationModal({
                contactId: connection.targetObjectId,
                matterId,
              });
            }}
          >
            <Typography size="sm" className="flex items-center gap-x-1">
              <ContactPhoneIcon className="h-4 w-4" />
              {phone?.number ? (
                formatLocalOrIntlPhoneNumber(phone)
              ) : (
                <Typography variant="subHeading">No phone number</Typography>
              )}
            </Typography>
          </Button>
        </PopoverContent>
      </Popover>
    </div>
  );
};
